// Slides du Rendez-vous Préalable · remise de l'AFFI (départ en conduite accompagnée)
// Se place AVANT le RVP 1 dans le parcours AAC. Élève + accompagnateur + enseignant référent.

export const RDV_PREALABLE_SLIDES = [
  {
    type: 'title',
    kicker: 'AAC · Le point de départ',
    title: 'Rendez-vous préalable',
    subtitle: 'Remise de l\'AFFI — 2 heures pour bien démarrer la conduite accompagnée. Élève, accompagnateur(s) et enseignant référent réunis.',
  },
  { type: 'section', number: 1, title: 'Pourquoi ce rendez-vous ?', note: 'Obligatoire · en fin de formation initiale' },
  {
    type: 'content',
    title: 'Trois objectifs concrets',
    bullets: [
      'Valider que l\'élève maîtrise les bases techniques SANS double commande',
      'Former l\'accompagnateur aux bonnes pratiques — éviter le sur-guidage et les conflits',
      'Signer l\'AFFI (Attestation de Fin de Formation Initiale) : le document qui autorise légalement la conduite accompagnée',
    ],
    footnote: 'Ce n\'est PAS un examen : pas d\'inspecteur, c\'est le formateur habituel. Tout se passe dans la bonne humeur !',
  },
  {
    type: 'stat',
    number: '0 km',
    label: 'C\'est ici que tout commence : aucune conduite en autonomie avant l\'AFFI',
    source: 'service-public.fr · fiche F2826',
    context: 'Sans AFFI transmise à l\'assureur et validée par lettre-avenant, l\'élève n\'est PAS couvert.',
  },
  {
    type: 'content',
    title: 'Les conditions pour être là aujourd\'hui',
    bullets: [
      'ETG (Code de la route) obtenu depuis moins de 5 ans',
      'Formation initiale terminée : 20 h minimum en boîte manuelle (13 h en boîte automatique)',
      'Étapes du livret d\'apprentissage validées',
      'Aval de l\'enseignant : le quota d\'heures ne suffit pas, le niveau technique doit être là',
    ],
  },
  { type: 'section', number: 2, title: 'Le rôle de l\'accompagnateur', note: 'Guider, pas enseigner' },
  {
    type: 'content',
    title: 'Qui peut être accompagnateur ?',
    bullets: [
      'Permis B valide depuis au moins 5 ans, sans interruption',
      'Accord ÉCRIT préalable de l\'assureur (extension de garantie AAC)',
      'Pas d\'annulation ou d\'invalidation du permis dans les 5 dernières années',
      'Le lien de parenté n\'importe pas : parent, frère, sœur, oncle, grand-parent…',
    ],
    footnote: 'Plusieurs accompagnateurs possibles — chacun désigné au contrat et validé par l\'assureur.',
  },
  {
    type: 'content',
    title: 'Les obligations pendant la phase AAC',
    bullets: [
      'Être assis À L\'AVANT, à côté de l\'élève',
      'Minimum 1 AN de conduite accompagnée ET 3 000 km parcourus (les deux !)',
      'Conduite en France uniquement',
      'Varier les conditions : ville, route, autoroute, nuit, pluie…',
      'Signe « conduite accompagnée » apposé à l\'arrière GAUCHE du véhicule',
    ],
  },
  {
    type: 'content',
    title: 'Les vitesses de l\'élève (conducteur novice)',
    bullets: [
      '110 km/h sur autoroute limitée à 130',
      '100 km/h sur les autres sections d\'autoroute',
      '100 km/h sur routes à 2 chaussées séparées par un terre-plein',
      '80 km/h sur les autres routes',
      '50 km/h en agglomération',
    ],
    footnote: 'Ces limitations s\'appliquent pendant toute la conduite accompagnée ET la période probatoire.',
  },
  {
    type: 'qcm',
    question: 'Sur une autoroute limitée à 130 km/h, l\'élève en conduite accompagnée roule au maximum à…',
    options: [
      { text: '130 km/h, comme tout le monde' },
      { text: '110 km/h', correct: true },
      { text: '90 km/h' },
    ],
    explanation: 'Les limitations « conducteur novice » s\'appliquent : 110 au lieu de 130, 100 au lieu de 110, 80 au lieu de 90.',
    engagement: 'Accompagnateur : c\'est aussi à vous d\'y veiller pendant les trajets !',
  },
  {
    type: 'content',
    title: 'Éviter le sur-guidage',
    bullets: [
      'L\'accompagnateur GUIDE verbalement, il ne refait pas la formation',
      'Trop d\'instructions = stress, conflits et mauvaises habitudes',
      'Pendant la partie pratique d\'aujourd\'hui : l\'enseignant démontre ses techniques pédagogiques in situ',
      'L\'accompagnateur observe depuis l\'arrière et apprend à formuler sans générer de panique',
    ],
  },
  { type: 'section', number: 3, title: 'Responsabilités & infractions', note: 'Qui paie quoi ?' },
  {
    type: 'content',
    title: 'Le principe',
    bullets: [
      'L\'élève est le « conducteur » légal (art. L121-1) — mais il ne peut PAS perdre de points : il n\'a pas encore le permis',
      'Radar automatique → le titulaire de la carte grise reçoit l\'avis : amende + points (sauf contestation)',
      'Contrôle avec interception → l\'accompagnateur peut être sanctionné s\'il a manqué de vigilance',
      'Délit grave → c\'est le juge qui tranche au cas par cas',
    ],
  },
  {
    type: 'qcm',
    question: 'Flash radar pendant un trajet en conduite accompagnée : qui reçoit l\'avis de contravention ?',
    options: [
      { text: 'L\'élève' },
      { text: 'Le titulaire de la carte grise', correct: true },
      { text: 'L\'auto-école' },
    ],
    explanation: 'Présomption administrative : le propriétaire du véhicule paie et perd les points, sauf s\'il conteste en désignant la personne qui encadrait l\'élève.',
  },
  {
    type: 'content',
    title: 'Alcool & stupéfiants : tolérance zéro',
    bullets: [
      'Accompagnateur alcoolisé 0,5–0,79 g/L : 135 €, −6 points, suspension possible — et perte du statut d\'accompagnateur',
      '≥ 0,8 g/L ou refus de test : DÉLIT — jusqu\'à 2 ans de prison et 4 500 €',
      'Stupéfiants : délit systématique, aucun seuil de tolérance',
      'Pour l\'élève : inscription au dossier préfecture — le juge peut interdire de passer l\'examen',
    ],
    footnote: 'L\'alcool est presque toujours une exclusion de garantie pour l\'assurance.',
  },
  { type: 'section', number: 4, title: 'Après le rendez-vous', note: 'Les démarches et la suite' },
  {
    type: 'content',
    title: 'AFFI signée : les démarches immédiates',
    bullets: [
      'Transmettre l\'AFFI à l\'assureur',
      'NE PAS CONDUIRE avant le retour écrit de la lettre-avenant (extension AAC validée)',
      'Apposer le disque « conduite accompagnée » à l\'arrière gauche',
      'À bord lors d\'un contrôle : carte grise, demande de permis de l\'élève, permis de l\'accompagnateur',
    ],
  },
  {
    type: 'content',
    title: 'La suite du parcours AAC',
    bullets: [
      'RVP 1 (≈ 1 000 km) : entre 4 et 6 mois après aujourd\'hui — 3h : 1h de conduite + 2h de théorie',
      'RVP 2 (≥ 3 000 km ET ≥ 1 an) : bilan final et préparation à l\'examen',
      'Examen pratique possible dès 17 ans',
      'Bonus AAC : période probatoire réduite à 2 ans au lieu de 3',
    ],
  },
  {
    type: 'qcm',
    question: 'L\'AFFI vient d\'être signée. Quand l\'élève peut-il commencer à conduire avec son accompagnateur ?',
    options: [
      { text: 'Dès ce soir, l\'AFFI suffit' },
      { text: 'Après le retour écrit de la lettre-avenant de l\'assureur', correct: true },
      { text: 'Après le RVP 1' },
    ],
    explanation: 'Tant que l\'assureur n\'a pas validé l\'avenant par écrit, l\'élève n\'est pas assuré. C\'est LE point bloquant à ne jamais négliger.',
  },
  {
    type: 'engagement',
    title: 'Nos engagements pour la conduite accompagnée',
    items: [
      'Élève : je respecte les limitations novice et je note chaque trajet dans le livret',
      'Accompagnateur : je guide sans sur-guider, jamais d\'alcool avant de monter en voiture',
      'Ensemble : on varie les conditions (nuit, pluie, autoroute) et on vise 3 000 km sereins',
      'On planifie le RVP 1 entre 4 et 6 mois — environ 1 000 km',
    ],
    signature: 'Élève · Accompagnateur(s) · Enseignant référent',
  },
  {
    type: 'cta',
    title: 'Place à la pratique !',
    description: '1 heure de conduite en circulation : l\'élève au volant sans double commande, l\'enseignant coache l\'accompagnateur installé à l\'arrière. La grille d\'évaluation « RDV Préalable · AFFI » (onglet Bilans) permet de valider les 20 critères et de partager le bilan par QR aux accompagnateurs.',
    next_action: 'Bilans → RDV Préalable · AFFI',
  },
];
